import React from 'react'
import { motion } from 'framer-motion'
import { Factory, Building2, Stethoscope, Briefcase, ShoppingCart, Warehouse, Hotel, GraduationCap, Landmark, Rocket, CheckCircle, ClipboardList, Search, UserCheck, Handshake, Users, HeadphonesIcon } from 'lucide-react'
import { Link } from 'react-router-dom'

const IndustrySolutions = () => {
  const industries = [
    { icon: <Factory className="w-10 h-10" />, title: 'Manufacturing Industries', description: 'Skilled & semi-skilled workforce for production, assembly, and quality control.' },
    { icon: <Building2 className="w-10 h-10" />, title: 'Construction Companies', description: 'Laborers, supervisors, and site support staff for construction projects.' },
    { icon: <Stethoscope className="w-10 h-10" />, title: 'Hospitals & Healthcare', description: 'Ward boys, nursing assistants, housekeeping, and administrative support.' },
    { icon: <Briefcase className="w-10 h-10" />, title: 'Corporate Offices', description: 'Office boys, housekeeping, front desk, and facility management staff.' },
    { icon: <ShoppingCart className="w-10 h-10" />, title: 'Retail & Shopping Chains', description: 'Sales associates, store helpers, and inventory management personnel.' },
    { icon: <Warehouse className="w-10 h-10" />, title: 'Logistics & Warehousing', description: 'Delivery boys, warehouse helpers, and logistics support staff.' },
    { icon: <Hotel className="w-10 h-10" />, title: 'Hospitality & Hotels', description: 'Housekeeping, kitchen helpers, front office, and event support staff.' },
    { icon: <GraduationCap className="w-10 h-10" />, title: 'Educational Institutions', description: 'Administrative staff, housekeeping, security, and support personnel.' },
    { icon: <Landmark className="w-10 h-10" />, title: 'Banking & Financial Services', description: 'Office support, data entry operators, and back-office staff.' },
    { icon: <Rocket className="w-10 h-10" />, title: "Startups & SME's", description: 'Flexible workforce solutions tailored for growing businesses.' }
  ]

  const whyChoose = [
    'Quick turnaround time',
    'Verified candidate database',
    'Transparent pricing',
    'Statutory compliance expertise',
    'Dedicated relationship manager',
    'Customized workforce solutions'
  ]

  const process = [
    { step: '01', title: 'Requirement Understanding', description: 'We discuss your workforce needs, timelines, and specific requirements in detail.' },
    { step: '02', title: 'Talent Sourcing', description: 'Our team sources qualified candidates from our verified database and networks.' },
    { step: '03', title: 'Screening & Shortlisting', description: 'Rigorous screening and background verification to shortlist the best candidates.' },
    { step: '04', title: 'Client Interview Coordination', description: 'We coordinate interviews and facilitate seamless communication between you and candidates.' },
    { step: '05', title: 'Deployment & Onboarding', description: 'Selected candidates are deployed with proper documentation and onboarding support.' },
    { step: '06', title: 'Continuous Support', description: 'Ongoing support with replacement guarantees and regular performance follow-ups.' }
  ]

  return (
    <div>
      {/* Hero */}
      <section className="bg-gradient-to-br from-primary-800 via-primary-700 to-teal-700 text-white py-16 md:py-20">
        <div className="container-max">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-4">Industry Solutions</h1>
            <p className="text-lg md:text-xl text-primary-100">Specialized staffing solutions across multiple industries</p>
            <div className="w-20 h-1 bg-accent-500 mx-auto mt-6 rounded-full"></div>
          </div>
        </div>
      </section>

      {/* Industries We Serve */}
      <section className="section-padding">
        <div className="container-max">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-secondary-900 mb-4">Industries We Serve</h2>
            <p className="text-xl text-secondary-600 max-w-2xl mx-auto">Delivering reliable manpower solutions across diverse sectors</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {industries.map((industry, index) => (
              <motion.div
                key={industry.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow border border-secondary-100"
              >
                <div className="text-primary-600 mb-4">{industry.icon}</div>
                <h3 className="text-xl font-semibold text-secondary-900 mb-2">{industry.title}</h3>
                <p className="text-secondary-600">{industry.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section-padding bg-secondary-50">
        <div className="container-max">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-secondary-900 mb-4">Why Choose Shrijaa Outsourcing Consultants?</h2>
            <p className="text-xl text-secondary-600 max-w-2xl mx-auto">What sets us apart from the rest</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {whyChoose.map((item, index) => (
              <motion.div
                key={item}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, delay: index * 0.08 }}
                viewport={{ once: true }}
                className="flex items-center gap-3 bg-white rounded-lg p-4 shadow-md"
              >
                <CheckCircle className="w-6 h-6 text-primary-600 flex-shrink-0" />
                <span className="text-secondary-800 font-medium">{item}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Process */}
      <section className="section-padding">
        <div className="container-max">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-secondary-900 mb-4">Our Process</h2>
            <p className="text-xl text-secondary-600 max-w-2xl mx-auto">A streamlined approach to deliver the right workforce</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {process.map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.08 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-primary-600 rounded-full flex items-center justify-center text-white font-bold text-xl mx-auto mb-4">
                  {item.step}
                </div>
                <h3 className="text-xl font-semibold text-primary-800 mb-3">{item.title}</h3>
                <p className="text-secondary-600">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-primary-600">
        <div className="container-max text-center text-white">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Need Workforce for Your Industry?</h2>
          <p className="text-xl text-primary-100 mb-8 max-w-2xl mx-auto">Get in touch with us today and let us handle your manpower needs.</p>
          <Link to="/contact" className="inline-flex items-center bg-white text-primary-700 px-8 py-3 rounded-full font-semibold hover:bg-primary-50 transition-colors">
            Contact Us
          </Link>
        </div>
      </section>
    </div>
  )
}

export default IndustrySolutions
